import React from 'react';
import DOMPurify from 'dompurify';
import { useParams } from 'react-router-dom';
import {
  useFeedbackItems,
  useSubmitFeedbackResponses,
  useFeedbackLastCompleted,
  useFeedbackLaunch,
  useFeedbackView,
} from '../../../../hooks/useMoodleHooks';
import { MoodleInstanceKey } from '../../../../support/slices/moodleSlice';

type Props = {
  module: any;
  instance: MoodleInstanceKey;
};

const INPUT_TYPES = new Set(['multichoice', 'multichoicerated', 'textfield', 'textarea']);

function parseMultichoiceOptions(presentation: string): string[] {
  if (!presentation) return [];
  const optionsPart = presentation.split('>>>>>').pop() || '';
  return optionsPart
    .split(/\||\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function groupItemsByMoodlePage(items: any[]) {
  const pages: { moodlePage: number; items: any[] }[] = [];
  let current: any[] = [];
  let moodlePage = 0;

  for (const item of items) {
    if (item.typ === 'pagebreak') {
      pages.push({ moodlePage, items: current });
      moodlePage += 1;
      current = [];
    } else if (item.hasvalue) {
      current.push(item);
    }
  }

  pages.push({ moodlePage, items: current });
  return pages;
}

function getErrorCode(error: any): string | undefined {
  return (
    error?.errorcode ||
    error?.response?.data?.errorcode ||
    error?.data?.errorcode
  );
}

function getErrorDetail(error: any): string | undefined {
  return (
    error?.detail ||
    error?.response?.data?.detail ||
    error?.data?.detail ||
    error?.message
  );
}

const MoodleFeedbackResource: React.FC<Props> = ({ module, instance }) => {
  const { courseId: routeCourseIdStr } = useParams<{ courseId: string }>();

  const feedbackId = Number(module?.instance ?? module?.id);

  const routeCourseId = Number.parseInt(routeCourseIdStr || '', 10);
  const moduleCourseId = Number(module?.course ?? module?.courseid);

  const courseId =
    Number.isFinite(moduleCourseId) && moduleCourseId > 0
      ? moduleCourseId
      : Number.isFinite(routeCourseId) && routeCourseId > 0
        ? routeCourseId
        : NaN;

  const [moodlePage, setMoodlePage] = React.useState(0);
  const [isFinished, setIsFinished] = React.useState(false);
  const [completionContent, setCompletionContent] = React.useState('');
  const [form, setForm] = React.useState<Record<string, any>>({});
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});

  const hasValidFeedbackId = Number.isFinite(feedbackId) && feedbackId > 0;
  const hasValidCourseId = Number.isFinite(courseId) && courseId > 0;

  const {
    data: lastCompletedData,
    error: lastCompletedError,
  } = useFeedbackLastCompleted(feedbackId, instance);

  const allItemsQuery = useFeedbackItems(
    feedbackId,
    hasValidCourseId ? { courseid: courseId } : undefined,
    instance
  );

  const submitMutation = useSubmitFeedbackResponses(feedbackId, instance);
  const launchMutation = useFeedbackLaunch(feedbackId, instance);
  const viewMutation = useFeedbackView(feedbackId, instance);

  const lastCompletedErrorCode = getErrorCode(lastCompletedError);
  const itemsErrorCode = getErrorCode(allItemsQuery.error);
  const submitErrorCode = getErrorCode(submitMutation.error);
  const launchErrorCode = getErrorCode(launchMutation.error);

  const isNotCompletedYet = lastCompletedErrorCode === 'not_completed_yet';
  const hasNoItemsConfigured =
    itemsErrorCode === 'no_items_available_yet' ||
    launchErrorCode === 'no_items_available_yet' ||
    submitErrorCode === 'no_items_available_yet';

  const isAlreadyComplete =
    !!lastCompletedData?.completed || !!module.completiondata?.isoverallcomplete;

  const unavailable =
    module.completiondata?.uservisible === false || module.uservisible === false;

  const launchStartedRef = React.useRef(false);
  const autoSubmittedBlankPagesRef = React.useRef<Set<number>>(new Set());

  const pages = React.useMemo(() => {
    if (!allItemsQuery.data?.items) return [];
    return groupItemsByMoodlePage(allItemsQuery.data.items);
  }, [allItemsQuery.data]);

  React.useEffect(() => {
    if (!pages.length) return;
    if (!hasValidCourseId) return;
    if (!hasValidFeedbackId) return;
    if (submitMutation.isPending) return;
    if (hasNoItemsConfigured) return;

    const pageObj = pages[moodlePage];
    if (!pageObj) return;

    if (pageObj.items.length === 0 && !autoSubmittedBlankPagesRef.current.has(moodlePage)) {
      autoSubmittedBlankPagesRef.current.add(moodlePage);

      submitMutation.mutate(
        {
          page: moodlePage,
          responses: [],
          courseid: courseId,
        },
        {
          onSuccess: (data) => {
            if (data?.completed) {
              setIsFinished(true);
              setCompletionContent(data.completionpagecontents || '');
              return;
            }

            if (typeof data?.jumpto === 'number' && data.jumpto !== moodlePage) {
              setMoodlePage(data.jumpto);
            }
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, moodlePage, courseId, hasValidCourseId, hasValidFeedbackId, hasNoItemsConfigured]);

  React.useEffect(() => {
    if (!hasValidFeedbackId) return;
    if (isAlreadyComplete || unavailable || !hasValidCourseId || launchStartedRef.current) {
      return;
    }

    if (hasNoItemsConfigured) return;

    launchStartedRef.current = true;

    launchMutation.mutate(courseId, {
      onSuccess: () => {
        viewMutation.mutate({ moduleviewed: true });
      },
    });
  }, [
    hasValidFeedbackId,
    isAlreadyComplete,
    unavailable,
    hasValidCourseId,
    courseId,
    launchMutation,
    viewMutation,
    hasNoItemsConfigured,
  ]);

  const currentPageObj = pages[moodlePage];
  const currentItems = currentPageObj?.items ?? [];
  const hasPrevPage = moodlePage > 0;
  const hasNextPage = moodlePage < pages.length - 1;

  function validate(pageItems: any[]) {
    const errors: Record<string, string> = {};

    pageItems.forEach((item: any) => {
      if (!INPUT_TYPES.has(item.typ)) return;
      if (item.required && (form[item.id] == null || form[item.id] === '')) {
        errors[item.id] = 'This field is required.';
      }
    });

    return errors;
  }

  function onChange(id: string | number, value: any) {
    setForm((prev) => ({ ...prev, [id]: value }));
    setTouched((prev) => ({ ...prev, [id]: true }));
  }

  function handlePageSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!hasValidCourseId) {
      alert('Missing course context. Please reload and try again.');
      return;
    }

    if (!hasValidFeedbackId) {
      alert('Missing feedback context. Please reload and try again.');
      return;
    }

    if (hasNoItemsConfigured) {
      return;
    }

    const errors = validate(currentItems);

    setTouched((prev) => {
      const all = { ...prev };
      currentItems.forEach((item: any) => {
        all[item.id] = true;
      });
      return all;
    });

    if (Object.keys(errors).length > 0) return;

    const responses = currentItems
      .filter((item: any) => INPUT_TYPES.has(item.typ) && form[item.id] !== undefined)
      .map((item: any) => ({
        name: `${item.typ}_${item.id}`,
        value: form[item.id],
      }));

    const payload: any = {
      page: moodlePage,
      responses,
      courseid: courseId,
    };

    if (!hasNextPage) payload.gofinish = 1;

    submitMutation.mutate(payload, {
      onSuccess: (data) => {
        if (data?.completed) {
          setIsFinished(true);
          setCompletionContent(data.completionpagecontents || '');
        } else if (typeof data?.jumpto === 'number') {
          setMoodlePage(data.jumpto);
        }
      },
      onError: (error) => {
        const errorCode = getErrorCode(error);
        if (errorCode === 'no_items_available_yet') {
          return;
        }
        alert('A network error occurred. Please try again.');
      },
    });
  }

  function handleBack() {
    if (!hasPrevPage || submitMutation.isPending) return;
    setMoodlePage((p) => Math.max(0, p - 1));
  }

  if (!hasValidFeedbackId) {
    return <div style={{ color: 'red' }}>Invalid feedback context.</div>;
  }

  if (isAlreadyComplete || isFinished) {
    return (
      <div style={{ margin: '1.5em 0' }}>
        <div
          style={{
            color: 'green',
            marginTop: 24,
            fontWeight: 600,
            fontSize: '1.1em',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span role="img" aria-label="complete" style={{ fontSize: 24 }}>
            ✅
          </span>
          Feedback submitted!
        </div>
        {completionContent && <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(completionContent) }} />}
      </div>
    );
  }

  if (unavailable) {
    return (
      <div style={{ color: '#b23c17', marginTop: 24, fontWeight: 500 }}>
        This feedback is not currently available.
      </div>
    );
  }

  if (!hasValidCourseId) {
    return (
      <div style={{ color: '#b23c17', marginTop: 24, fontWeight: 500 }}>
        Missing course context for this feedback.
      </div>
    );
  }

  if (hasNoItemsConfigured) {
    return (
      <div style={{ marginTop: 24, color: '#8a5a00', fontWeight: 500 }}>
        This feedback exists, but no questions have been set up yet.
      </div>
    );
  }

  if (allItemsQuery.isLoading) {
    return <div>Loading feedback form…</div>;
  }

  if (allItemsQuery.error && itemsErrorCode !== 'no_items_available_yet') {
    return (
      <div style={{ color: 'red' }}>
        {getErrorDetail(allItemsQuery.error) || 'Unable to load feedback form.'}
      </div>
    );
  }

  if (!pages.length) {
    return <div>No feedback questions found for this feedback.</div>;
  }

  if (!currentItems.length) {
    return <div>Processing, please wait…</div>;
  }

  return (
    <div style={{ margin: '1.5em 0' }}>
      <form onSubmit={handlePageSubmit} style={{ marginTop: 24 }}>
        {currentItems.map((item: any) => {
          const val = form[item.id] ?? '';
          const isTouched = touched[item.id];
          const showError = isTouched && item.required && (val == null || val === '');

          return (
            <div key={item.id} style={{ marginBottom: 18 }}>
              <label>
                <strong>
                  {item.name}
                  {item.required && <span style={{ color: 'red' }}> *</span>}
                </strong>

                <div>
                  {item.typ === 'textarea' && (
                    <textarea
                      value={val}
                      onChange={(e) => onChange(item.id, e.target.value)}
                      style={{ width: '100%', minHeight: 60, padding: 6 }}
                    />
                  )}

                  {item.typ === 'textfield' && (
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => onChange(item.id, e.target.value)}
                      style={{ width: '100%', padding: 6 }}
                      autoComplete="off"
                    />
                  )}

                  {(item.typ === 'multichoice' || item.typ === 'multichoicerated') && (
                    <select
                      value={val}
                      onChange={(e) => onChange(item.id, e.target.value)}
                      style={{ width: '100%', padding: 6 }}
                    >
                      <option value="">-- Select --</option>
                      {parseMultichoiceOptions(item.presentation).map((opt, idx) => (
                        <option key={idx} value={idx}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </label>

              {showError && (
                <div style={{ color: 'red', fontSize: '0.95em' }}>
                  This field is required.
                </div>
              )}
            </div>
          );
        })}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleBack}
            disabled={!hasPrevPage || submitMutation.isPending}
          >
            Previous
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitMutation.isPending}
          >
            {submitMutation.isPending
              ? 'Submitting...'
              : hasNextPage
                ? 'Next'
                : 'Submit Feedback'}
          </button>
        </div>

        {submitMutation.isError && submitErrorCode !== 'no_items_available_yet' && (
          <div style={{ color: 'red', marginTop: 8 }}>
            {getErrorDetail(submitMutation.error) || 'Submission failed. Please try again.'}
          </div>
        )}

        {lastCompletedError && !isNotCompletedYet && (
          <div style={{ color: 'red', marginTop: 8 }}>
            {getErrorDetail(lastCompletedError) || 'Unable to verify previous completion state.'}
          </div>
        )}
      </form>
    </div>
  );
};

export default MoodleFeedbackResource;